/**
 * Dialog for cloning a database to a new database on the same or different connection.
 * Supports MSSQL, PostgreSQL, and SQLite (same-engine only).
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { Copy, Database, FolderOpen } from 'lucide-react';
import { CloneDatabaseProgressDialog } from './CloneDatabaseProgressDialog';
import { PersonalModeCloneDatabaseLimitMessage } from './PersonalModeCloneDatabaseLimitMessage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from '../../../components/ui/toaster';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useProFeature, UpgradeDialog } from '../../../core/licensing';
import { useAppStore } from '../../../store/appStore';
import {
  checkPersonalModeLimits,
  fetchDatabaseSize,
  type PersonalModeLimitCheck,
} from '../utils/personalModeLimits';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { safeConnectionName } from '@/types/safeConnectionDisplay';
type CloneDatabaseDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  databaseName: string;
  onRefreshDatabases?: (connectionId: string) => void;
};

type CloneFormValues = {
  targetConnectionId: string;
  targetDatabaseName: string;
  includeData: boolean;
  includeSecurityObjects: boolean;
  targetFilePath: string;
  targetConnectionAlias: string;
};

export function CloneDatabaseDialog({
  open,
  onOpenChange,
  connectionId,
  databaseName,
  onRefreshDatabases,
}: CloneDatabaseDialogProps) {
  const { connections, activeConnections } = useConnectionStore();
  const pgsqlToolsSettings = useAppStore((state) => state.pgsqlToolsSettings);
  const pgsqlToolsBinPath = pgsqlToolsSettings.customBinPath?.trim() || undefined;
  const [isCloning, setIsCloning] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [cloneTargetName, setCloneTargetName] = useState<string>('');
  const [cloneTargetConnectionId, setCloneTargetConnectionId] = useState<string>(connectionId);

  // License check for clone with data
  const { canUse: canUseFullClone } = useProFeature('clone-database-full');
  const [isCheckingSize, setIsCheckingSize] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [limitCheck, setLimitCheck] = useState<PersonalModeLimitCheck | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Get source connection details
  const sourceConnection = useMemo(() => {
    const saved = connections.find((c) => c.id === connectionId);
    if (saved) return saved;
    return activeConnections.find((c) => c.id === connectionId)?.connection;
  }, [connections, activeConnections, connectionId]);

  const sourceEngine = (sourceConnection?.engine || 'mssql') as DatabaseEngineId;
  const isSqlite = sourceEngine === 'sqlite';
  const isMssql = sourceEngine === 'mssql' || sourceEngine === 'mock';

  useEffect(() => {
    if (!open) return;
    setCloneTargetConnectionId(connectionId);
  }, [open, connectionId]);

  // Filter connections to same engine only
  const sameEngineConnections = useMemo(() => {
    // For active connections, we need to find their connection configs
    const activeWithConfigs = activeConnections
      .map((ac) => {
        const config = connections.find((c) => c.id === ac.id) || ac.connection;
        return { ...ac, connection: config };
      })
      .filter((ac) => {
        const engine = ac.connection.engine || 'mssql';
        return engine === sourceEngine && ac.status !== 'disconnected';
      });

    return activeWithConfigs;
  }, [activeConnections, connections, sourceEngine]);

  const form = useForm({
    defaultValues: {
      targetConnectionId: connectionId, // Default to same connection
      targetDatabaseName: `${databaseName}_clone`,
      includeData: isMssql,
      includeSecurityObjects: false,
      targetFilePath: isSqlite
        ? sourceConnection?.filePath?.replace(/\.db$|\.sqlite$/, '_clone.db') || ''
        : '',
      targetConnectionAlias: '',
    } as CloneFormValues,
    onSubmit: async ({ value }) => {
      await handleClone(value);
    },
  });

  // Note: targetConnectionId will be used when actual clone IPC is implemented
  const _targetConnectionId = useStore(form.store, (state) => state.values.targetConnectionId);
  void _targetConnectionId; // Suppress unused variable warning until clone backend is implemented
  const targetDatabaseName = useStore(form.store, (state) => state.values.targetDatabaseName);
  const targetFilePath = useStore(form.store, (state) => state.values.targetFilePath);

  // Validation helpers
  const validateDatabaseName = (name: string): string | undefined => {
    if (!name.trim()) {
      return 'Database name is required';
    }
    // Basic identifier validation - alphanumeric, underscore, no leading numbers
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
      return 'Invalid database name. Use letters, numbers, and underscores only.';
    }
    return undefined;
  };

  const validateFilePath = (path: string): string | undefined => {
    if (!path.trim()) {
      return 'File path is required';
    }
    // Basic path validation
    if (!path.endsWith('.db') && !path.endsWith('.sqlite') && !path.endsWith('.sqlite3')) {
      return 'File should have .db, .sqlite, or .sqlite3 extension';
    }
    return undefined;
  };

  const handleClone = async (values: CloneFormValues) => {
    const effectiveIncludeData = isMssql ? true : values.includeData;

    // Validate based on engine
    if (isSqlite) {
      const pathError = validateFilePath(values.targetFilePath);
      if (pathError) {
        toast.error({ title: 'Validation Error', description: pathError });
        return;
      }
    } else {
      const nameError = validateDatabaseName(values.targetDatabaseName);
      if (nameError) {
        toast.error({ title: 'Validation Error', description: nameError });
        return;
      }
    }

    if (!window.electronAPI?.cloneDatabase) {
      toast.error({
        title: 'Clone Failed',
        description: 'Clone database API not available',
      });
      return;
    }

    // License check: if including data and not Pro, check database size
    let preflightDatabaseSizeBytes: number | undefined;
    if (effectiveIncludeData && !canUseFullClone && !isSqlite) {
      setIsCheckingSize(true);
      try {
        const dbType = sourceEngine === 'pgsql' ? 'pgsql' : 'mssql';
        const sizeResult = await fetchDatabaseSize(connectionId, databaseName, dbType);

        if (sizeResult.success && sizeResult.sizeInBytes !== undefined) {
          preflightDatabaseSizeBytes = sizeResult.sizeInBytes;
          const check = checkPersonalModeLimits(sizeResult.sizeInBytes);
          if (check.exceedsLimit) {
            setLimitCheck(check);
            setShowLimitMessage(true);
            setIsCheckingSize(false);
            return;
          }
        }
        // If size check fails or size is within limit, proceed
      } catch (error) {
        console.error('[CloneDatabaseDialog] Size check error:', error);
        // On error, allow the operation to proceed (fail open)
      } finally {
        setIsCheckingSize(false);
      }
    }

    // Check if target already exists
    try {
      const checkResult = await window.electronAPI.cloneDatabase.checkTargetExists({
        engine: sourceEngine,
        connectionId: values.targetConnectionId,
        databaseName: isSqlite ? undefined : values.targetDatabaseName,
        filePath: isSqlite ? values.targetFilePath : undefined,
      });

      if (checkResult.exists) {
        toast.error({
          title: 'Target Already Exists',
          description: isSqlite
            ? `File "${values.targetFilePath}" already exists. Choose a different path.`
            : `Database "${values.targetDatabaseName}" already exists. Choose a different name.`,
        });
        return;
      }

      if (checkResult.error) {
        toast.error({
          title: 'Preflight Check Failed',
          description: checkResult.error,
        });
        return;
      }
    } catch (error) {
      toast.error({
        title: 'Preflight Check Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      return;
    }

    setIsCloning(true);

    try {
      const targetName = isSqlite ? values.targetFilePath : values.targetDatabaseName;
      setCloneTargetName(targetName);
      setCloneTargetConnectionId(values.targetConnectionId);

	      const response = await window.electronAPI.cloneDatabase.start({
	        sourceConnectionId: connectionId,
	        sourceDatabaseName: databaseName,
	        engine: sourceEngine,
	        targetConnectionId: values.targetConnectionId,
	        targetDatabaseName: values.targetDatabaseName,
	        includeData: effectiveIncludeData,
	        includeSecurityObjects: values.includeSecurityObjects,
	        targetFilePath: isSqlite ? values.targetFilePath : undefined,
	        targetConnectionAlias: isSqlite ? values.targetConnectionAlias : undefined,
	        pgsqlToolsBinPath: sourceEngine === 'pgsql' ? pgsqlToolsBinPath : undefined,
	        preflight: preflightDatabaseSizeBytes !== undefined ? { databaseSizeBytes: preflightDatabaseSizeBytes } : undefined,
	      });

      if (!response.success || !response.operationId) {
        toast.error({
          title: 'Clone Failed',
          description: response.error || 'Failed to start clone operation',
        });
        setIsCloning(false);
        return;
      }

      // Switch to progress dialog
      setOperationId(response.operationId);
      setShowProgress(true);
    } catch (error) {
      toast.error({
        title: 'Clone Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
      setIsCloning(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isCloning && !showProgress) {
      form.reset();
      setOperationId(null);
      setShowProgress(false);
      setCloneTargetName('');
      setShowLimitMessage(false);
      setLimitCheck(null);
      onOpenChange(false);
    }
  }, [isCloning, showProgress, form, onOpenChange]);

  // Reset limit state when dialog opens
  useEffect(() => {
    if (open) {
      setShowLimitMessage(false);
      setLimitCheck(null);
    }
  }, [open]);

  // Handler for continuing with schema-only clone
  const handleContinueSchemaOnly = useCallback(() => {
    form.setFieldValue('includeData', false);
    setShowLimitMessage(false);
    setLimitCheck(null);
  }, [form]);

  // Handler for upgrade button
  const handleUpgrade = useCallback(() => {
    setShowUpgradeDialog(true);
  }, []);

	  const handleProgressClose = useCallback(() => {
	    setShowProgress(false);
	    setOperationId(null);
	    setIsCloning(false);
	    setCloneTargetName('');
	    setCloneTargetConnectionId(connectionId);
	    form.reset();
	    onOpenChange(false);
	  }, [connectionId, form, onOpenChange]);

	  const handleBrowse = async () => {
	    // TODO: Implement file browse dialog
	    toast.default({
      title: 'Browse',
      description: 'File browser will be implemented. For now, type the path manually.',
    });
  };

  // Check if form is valid
  const isFormValid = useMemo(() => {
    if (isSqlite) {
      return !validateFilePath(targetFilePath);
    }
    return !validateDatabaseName(targetDatabaseName);
  }, [isSqlite, targetFilePath, targetDatabaseName]);

  // Show progress dialog when cloning
  if (showProgress && operationId) {
    return (
      <CloneDatabaseProgressDialog
        open={true}
        onClose={handleProgressClose}
        operationId={operationId}
        sourceDatabaseName={databaseName}
        targetDatabaseName={cloneTargetName}
        targetConnectionId={cloneTargetConnectionId}
        onRefreshDatabases={onRefreshDatabases}
      />
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent data-testid="clone-database-dialog" className="sm:max-w-[500px]">
          {/* Show limit message when Personal mode and large database */}
          {showLimitMessage && limitCheck ? (
            <PersonalModeCloneDatabaseLimitMessage
              limitCheck={limitCheck}
              onUpgrade={handleUpgrade}
              onContinueSchemaOnly={handleContinueSchemaOnly}
              onCancel={handleClose}
              schemaOnlyAvailable={!isMssql}
              schemaOnlyUnavailableMessage={
                isMssql ? 'SQL Server cloning uses a BACPAC export/import and always includes data.' : undefined
              }
            />
          ) : isCheckingSize ? (
            <div className="flex flex-col items-center justify-center p-8 gap-3">
              <RandomDotmSpinner size={32} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Checking database size...</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                    <Copy className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <DialogTitle>Clone Database</DialogTitle>
                    <DialogDescription className="mt-1">
                      Create a copy of &quot;{databaseName}&quot;
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <form
                id="clone-database-form"
                data-testid="clone-database-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void form.handleSubmit();
                }}
              >
                <div className="py-4 space-y-4">
                  {/* Source database (read-only) */}
                  <div className="space-y-2">
                    <Label>Source Database</Label>
                    <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
                      <Database className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {databaseName}
                        <span className="text-muted-foreground ml-2">
                          ({safeConnectionName(sourceConnection, 'Unknown')})
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Target connection (MSSQL/PGSQL only) */}
                  {!isSqlite && (
                    <div className="space-y-2">
                      <Label htmlFor="target-connection">Target Connection</Label>
                      <form.Field name="targetConnectionId">
                        {(field) => (
                          <Select
                            value={field.state.value}
                            onValueChange={(value) => field.handleChange(value)}
                          >
                            <SelectTrigger id="target-connection" data-testid="clone-target-connection">
                              <SelectValue placeholder="Select connection..." />
                            </SelectTrigger>
                            <SelectContent>
                              {sameEngineConnections.map((conn) => (
                                <SelectItem key={conn.id} value={conn.id}>
                                  {safeConnectionName(conn.connection)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                      <p className="text-xs text-muted-foreground">
                        Only {sourceEngine.toUpperCase()} connections are shown
                      </p>
                    </div>
                  )}

                  {/* Target database name (MSSQL/PGSQL) */}
                  {!isSqlite && (
                    <div className="space-y-2">
                      <Label htmlFor="target-database-name">New Database Name</Label>
                      <form.Field name="targetDatabaseName">
                        {(field) => (
                          <>
                            <Input
                              id="target-database-name"
                              value={field.state.value}
                              onChange={(e) => field.handleChange(e.target.value)}
                              placeholder="Enter database name"
                              data-testid="clone-target-database-name"
                            />
                            {field.state.value && validateDatabaseName(field.state.value) && (
                              <p className="text-xs text-destructive">
                                {validateDatabaseName(field.state.value)}
                              </p>
                            )}
                          </>
                        )}
                      </form.Field>
                    </div>
                  )}

                  {/* Target file path (SQLite) */}
                  {isSqlite && (
                    <div className="space-y-2">
                      <Label htmlFor="target-file-path">Target File Path</Label>
                      <form.Field name="targetFilePath">
                        {(field) => (
                          <div className="flex gap-2">
                            <Input
                              id="target-file-path"
                              value={field.state.value}
                              onChange={(e) => field.handleChange(e.target.value)}
                              placeholder="/path/to/new_database.db"
                              className="flex-1"
                              data-testid="clone-target-file-path"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={handleBrowse}
                              title="Browse..."
                              data-testid="clone-browse-button"
                            >
                              <FolderOpen className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </form.Field>
                      {targetFilePath && validateFilePath(targetFilePath) && (
                        <p className="text-xs text-destructive">{validateFilePath(targetFilePath)}</p>
                      )}
                    </div>
                  )}

                  {/* SQLite connection alias */}
                  {isSqlite && (
                    <div className="space-y-2">
                      <Label htmlFor="target-alias">Connection Alias (optional)</Label>
                      <form.Field name="targetConnectionAlias">
                        {(field) => (
                          <Input
                            id="target-alias"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder="My Cloned Database"
                            data-testid="clone-target-alias"
                          />
                        )}
                      </form.Field>
                    </div>
                  )}

                  {/* Include data checkbox */}
                  <form.Field name="includeData">
                    {(field) => (
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="include-data"
                            checked={isMssql ? true : field.state.value}
                            onCheckedChange={(checked) => field.handleChange(checked === true)}
                            disabled={isMssql}
                            data-testid="clone-include-data"
                          />
                          <Label
                            htmlFor="include-data"
                            className={`text-sm font-normal ${isMssql ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}
                          >
                            Include data (copy all rows)
                          </Label>
                        </div>
                        {isMssql && (
                          <p className="text-xs text-muted-foreground">
                            SQL Server cloning uses a BACPAC export/import and always includes data.
                          </p>
                        )}
                      </div>
                    )}
                  </form.Field>

                  {/* Include security objects checkbox (MSSQL only) */}
                  {isMssql && (
                    <form.Field name="includeSecurityObjects">
                      {(field) => (
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="include-security"
                              checked={field.state.value}
                              onCheckedChange={(checked) => field.handleChange(checked === true)}
                              data-testid="clone-include-security"
                            />
                            <Label htmlFor="include-security" className="text-sm font-normal cursor-pointer">
                              Include security objects (users, roles, permissions)
                            </Label>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Security objects aren’t included in BACPAC and are copied in a separate step.
                          </p>
                        </div>
                      )}
                    </form.Field>
                  )}
                </div>
              </form>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isCloning}
                  data-testid="clone-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="clone-database-form"
                  disabled={isCloning || !isFormValid}
                  data-testid="clone-submit-button"
                >
                  {isCloning ? 'Cloning...' : 'Clone'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />
    </>
  );
}
